import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SiteContentCtx = createContext({ content: {}, reload: () => {} });

const CSS_VAR_MAP = {
  primary_hsl: "--clinic-primary",
  primary_dark_hsl: "--clinic-primary-dark",
  secondary_hsl: "--clinic-secondary",
  secondary_dark_hsl: "--clinic-secondary-dark",
};

function applyTheme(theme) {
  if (!theme || typeof document === "undefined") return;
  const root = document.documentElement;
  Object.entries(CSS_VAR_MAP).forEach(([k, cssVar]) => {
    if (theme[k]) root.style.setProperty(cssVar, theme[k]);
  });
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState({});
  const load = useCallback(async () => {
    try {
      const r = await api.get("/site-content");
      setContent(r.data || {});
      applyTheme(r.data?.theme_colors);
    } catch (err) {
      console.warn("Site content load failed, using defaults:", err?.message || err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <SiteContentCtx.Provider value={{ content, reload: load }}>
      {children}
    </SiteContentCtx.Provider>
  );
}

export function useSiteContent(section) {
  const ctx = useContext(SiteContentCtx);
  if (section) return ctx.content?.[section] || {};
  return ctx;
}

// hex <-> hsl helpers for admin color picker
export function hexToHslString(hex) {
  const m = hex.replace("#", "");
  if (m.length !== 6) return "0 0% 0%";
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function darkenHslString(hslStr, amount = 12) {
  const parts = hslStr.split(/\s+/);
  if (parts.length < 3) return hslStr;
  const h = parseInt(parts[0], 10);
  const s = parseInt(parts[1], 10);
  const l = Math.max(0, parseInt(parts[2], 10) - amount);
  return `${h} ${s}% ${l}%`;
}
